import React, { useMemo } from 'react'
import { Paper, useTheme } from '@mui/material'
import Chart from 'react-apexcharts'
import moment from 'moment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import Text from '../../Text'

// Gráfico premium de área (ApexCharts) com gradiente e cabeçalho de resumo.
// Genérico e compartilhado: recebe séries + cores prontas, então serve tanto o
// relatório de envios (Sucesso vs Erro) quanto o do SmartFilter (Liberadas vs
// Bloqueadas). O eixo X vem de `categories` (datas ISO), formatado em DD/MM.
//
// props:
//   title       -> título do gráfico (ex.: "Envios por dia")
//   periodLabel -> subtítulo do período (ex.: "Últimos 30 dias")
//   categories  -> array de datas (YYYY-MM-DD ou ISO) para o eixo X
//   series      -> [{ name, color, data: number[] }]
//   headerStat  -> { label, value, good } (opcional) — a "pílula" de taxa no topo
export default function ReportChart({ title = 'Por dia', periodLabel, categories = [], series = [], headerStat, unitLabel = '', exportFilename = 'relatorio' }) {
  const theme = useTheme()

  const cats = (categories || []).map((date) => moment(date).format('DD/MM'))
  const colors = series.map((s) => s.color)
  const good = headerStat ? !!headerStat.good : true

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'area',
      height: 380,
      fontFamily: 'inherit',
      foreColor: theme.isDark ? '#94a3b8' : '#64748b',
      toolbar: {
        show: true,
        tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
        export: {
          csv: { filename: exportFilename },
          svg: { filename: exportFilename },
          png: { filename: exportFilename },
        },
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 600,
        animateGradually: { enabled: true, delay: 80 },
        dynamicAnimation: { enabled: true, speed: 300 },
      },
      dropShadow: { enabled: true, top: 3, left: 0, blur: 6, color: colors[0] || '#059669', opacity: 0.15 },
    },
    colors: colors.length ? colors : ['#059669', '#eb3c3c'],
    stroke: { curve: 'smooth', width: series.map((_, i) => (i === 0 ? 3 : 2)), lineCap: 'round' },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 90, 100] },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: theme.isDark ? '#334155' : '#e2e8f0',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { left: 8, right: 8 },
    },
    xaxis: {
      categories: cats,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontSize: '11px', fontWeight: 500 } },
    },
    yaxis: {
      labels: {
        style: { fontSize: '12px', fontWeight: 600 },
        formatter: (val) => {
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`
          return val
        },
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '13px',
      fontWeight: 600,
      markers: { size: 6, shape: 'circle' },
      itemMargin: { horizontal: 12 },
    },
    tooltip: {
      theme: theme.isDark ? 'dark' : 'light',
      shared: true,
      intersect: false,
      style: { fontSize: '13px' },
      y: { formatter: (val) => `${Number(val).toLocaleString('pt-BR')}${unitLabel ? ` ${unitLabel}` : ''}` },
      marker: { show: true },
    },
    markers: { size: 0, hover: { size: 6, sizeOffset: 3 } },
  }), [theme.isDark, cats, colors, series, unitLabel, exportFilename])

  const chartSeries = useMemo(
    () => series.map((s) => ({ name: s.name, data: s.data })),
    [series]
  )

  return (
    <div className="mt-2">
      <Paper
        elevation={5}
        sx={{ overflow: 'hidden', borderRadius: '12px', border: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}
      >
        <div
          className="flex items-center justify-between gap-3 px-5 py-4"
          style={{ borderBottom: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0' }}
        >
          <div className="min-w-0">
            <Text className="text-sm font-bold">{title}</Text>
            {periodLabel && <Text className="text-xs opacity-45 mt-0.5 block">{periodLabel}</Text>}
          </div>
          {
            headerStat &&
            <div
              className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full"
              style={{ background: good ? 'rgba(5,150,105,0.12)' : 'rgba(235,60,60,0.12)' }}
            >
              {
                good
                ? <TrendingUpIcon style={{ fontSize: 18, color: '#059669' }} />
                : <TrendingDownIcon style={{ fontSize: 18, color: '#eb3c3c' }} />
              }
              <Text className="text-sm font-black" style={{ color: good ? '#059669' : '#eb3c3c' }}>{headerStat.value}</Text>
              {headerStat.label && <Text className="hidden sm:block text-xs font-medium opacity-50">{headerStat.label}</Text>}
            </div>
          }
        </div>
        <div className="px-2 pt-2 pb-0">
          <Chart options={chartOptions} series={chartSeries} type="area" height={380} />
        </div>
      </Paper>
    </div>
  )
}
